import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consentOpen, setConsentOpen] = useState(false);

  const promptLogin = useCallback(() => {
    let alreadyAccepted = false;
    try {
      alreadyAccepted = !!window.localStorage.getItem("cgu_accepted_at");
    } catch (e) {
      // ignore
    }
    if (alreadyAccepted) {
      // CGU already accepted once → skip modal, go straight to OAuth
      const redirectUrl = window.location.origin + "/app";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
        redirectUrl
      )}`;
      return;
    }
    setConsentOpen(true);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!r.ok) throw new Error("not auth");
      const data = await r.json();
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      // swallow
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: checkAuth, logout, consentOpen, setConsentOpen, promptLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export { API };
